import { createElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { renderHook, waitFor } from '@testing-library/react';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { fetchQuoteKey, fetchQuote, useFetchQuote } from './fetch-quote';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  receivableInvoicesApiNamespace: '/api/v5',
}));

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

describe('unit | API | queries | fetch-quote', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const quoteId = 'quote-123';
  const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/quotes/shared`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe(fetchQuoteKey, () => {
    it('generates correct query key structure', () => {
      const result = fetchQuoteKey({ id: quoteId });

      expect(result).toStrictEqual(['quote', quoteId]);
    });

    it('generates unique keys for different IDs', () => {
      const key1 = fetchQuoteKey({ id: 'quote-1' });
      const key2 = fetchQuoteKey({ id: 'quote-2' });

      expect(key1).not.toStrictEqual(key2);
    });
  });

  describe(fetchQuote, () => {
    it('fetches quote by ID successfully', async () => {
      const apiResponse = {
        data: {
          id: quoteId,
          type: 'quotes',
          attributes: {
            amount_due: { value: '1000.00', currency: 'EUR' },
            contact_email: 'client@example.com',
            currency: 'EUR',
            customer_snapshot: {
              kind: 'company',
              name: 'Test Company',
              first_name: '',
              last_name: '',
              tin_number: '123456789',
              vat_number: 'FR123456789',
              address_line1: '123 Test Street',
              address_line2: '',
              address_zipcode: '75001',
              address_city: 'Paris',
              address_country: 'FR',
            },
            is_close_to_expire: false,
            is_pdf_generated: true,
            issue_date: '2025-01-01',
            linked_invoice: { number: 'INV-001' },
            locale: 'fr',
            number: 'Q-001',
            organization: {
              id: 'org-123',
              slug: 'test-org',
              legal_name: 'Test Organization',
              legal_form: 'SAS',
              legal_number: '123456789',
              legal_country: 'FR',
              vat_number: 'FR123456789',
              address_line1: '456 Org Street',
              address_line2: '',
              address_zipcode: '75002',
              address_city: 'Paris',
              address_country: 'FR',
            },
            pdf_url: 'https://example.com/quote.pdf',
            quote_versions: [
              {
                id: 'version-1',
                created_at: '2025-01-01T00:00:00Z',
                pdf_url: 'https://example.com/quote-v1.pdf',
              },
            ],
            remaining_days_until_expiry: 30,
            sections: [],
            stamp_duty_amount: { value: '0.00', currency: 'EUR' },
            status: 'pending_approval',
            subtotal: { value: '833.33', currency: 'EUR' },
            terms_and_conditions: 'Standard terms',
            vat_amount: { value: '166.67', currency: 'EUR' },
          },
          relationships: {
            receivable_invoicing_upload: {
              data: { id: 'upload-123', type: 'receivable-invoices-uploads' },
            },
            terms_conditions_upload: {
              data: { id: 'upload-456', type: 'receivable-invoices-uploads' },
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchQuote({ id: quoteId });

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${quoteId}`);
      expect(result).toHaveProperty('id', quoteId);
      expect(result).toHaveProperty('amountDue');
      expect(result).toHaveProperty('contactEmail', 'client@example.com');
      expect(result).toHaveProperty('receivableInvoicingUploadId', 'upload-123');
      expect(result).toHaveProperty('termsConditionsUploadId', 'upload-456');
      expect(result).toHaveProperty('isCloseToExpire', false);
      expect(result).toHaveProperty('isPdfGenerated', true);
      expect(result).toHaveProperty('issueDate', '2025-01-01');
      expect(result).toHaveProperty('linkedInvoice', { number: 'INV-001' });
      expect(result).toHaveProperty('locale', 'fr');
      expect(result).toHaveProperty('number', 'Q-001');
      expect(result).toHaveProperty('remainingDaysUntilExpiry', 30);
      expect(result).toHaveProperty('status', 'pending_approval');
      expect(result).toHaveProperty('termsAndConditions', 'Standard terms');
    });

    it('handles quote without relationships', async () => {
      const apiResponse = {
        data: {
          id: quoteId,
          type: 'quotes',
          attributes: {
            amount_due: { value: '200.00', currency: 'EUR' },
            contact_email: 'simple@example.com',
            currency: 'EUR',
            customer_snapshot: {
              kind: 'company',
              name: 'Simple Company',
              first_name: '',
              last_name: '',
              tin_number: '111222333',
              vat_number: 'FR111222333',
              address_line1: '111 Simple Street',
              address_line2: '',
              address_zipcode: '75005',
              address_city: 'Toulouse',
              address_country: 'FR',
            },
            is_close_to_expire: false,
            is_pdf_generated: true,
            issue_date: '2025-01-20',
            linked_invoice: { number: 'INV-003' },
            locale: 'fr',
            number: 'Q-003',
            organization: {
              id: 'org-789',
              slug: 'simple-org',
              legal_name: 'Simple Organization',
              legal_form: 'SAS',
              legal_number: '111222333',
              legal_country: 'FR',
              vat_number: 'FR111222333',
              address_line1: '222 Simple Org Street',
              address_line2: '',
              address_zipcode: '75006',
              address_city: 'Nice',
              address_country: 'FR',
            },
            pdf_url: 'https://example.com/simple-quote.pdf',
            quote_versions: [],
            remaining_days_until_expiry: 15,
            sections: [],
            stamp_duty_amount: { value: '0.00', currency: 'EUR' },
            status: 'canceled',
            subtotal: { value: '166.67', currency: 'EUR' },
            terms_and_conditions: 'Simple terms',
            vat_amount: { value: '33.33', currency: 'EUR' },
          },
          relationships: undefined,
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchQuote({ id: quoteId });

      expect(result).toHaveProperty('receivableInvoicingUploadId', undefined);
      expect(result).toHaveProperty('termsConditionsUploadId', undefined);
    });

    it('handles quote with partial relationships', async () => {
      const apiResponse = {
        data: {
          id: quoteId,
          type: 'quotes',
          attributes: {
            amount_due: { value: '300.00', currency: 'EUR' },
            contact_email: 'partial@example.com',
            currency: 'EUR',
            customer_snapshot: {
              kind: 'company',
              name: 'Partial Company',
              first_name: '',
              last_name: '',
              tin_number: '444555666',
              vat_number: 'FR444555666',
              address_line1: '333 Partial Street',
              address_line2: '',
              address_zipcode: '75007',
              address_city: 'Bordeaux',
              address_country: 'FR',
            },
            is_close_to_expire: false,
            is_pdf_generated: true,
            issue_date: '2025-01-25',
            linked_invoice: { number: 'INV-004' },
            locale: 'fr',
            number: 'Q-004',
            organization: {
              id: 'org-101',
              slug: 'partial-org',
              legal_name: 'Partial Organization',
              legal_form: 'SAS',
              legal_number: '444555666',
              legal_country: 'FR',
              vat_number: 'FR444555666',
              address_line1: '444 Partial Org Street',
              address_line2: '',
              address_zipcode: '75008',
              address_city: 'Nantes',
              address_country: 'FR',
            },
            pdf_url: 'https://example.com/partial-quote.pdf',
            quote_versions: [],
            remaining_days_until_expiry: 20,
            sections: [],
            stamp_duty_amount: { value: '0.00', currency: 'EUR' },
            status: 'pending_approval',
            subtotal: { value: '250.00', currency: 'EUR' },
            terms_and_conditions: 'Partial terms',
            vat_amount: { value: '50.00', currency: 'EUR' },
          },
          relationships: {
            receivable_invoicing_upload: {
              data: { id: 'upload-789', type: 'receivable-invoices-uploads' },
            },
            terms_conditions_upload: {
              data: null,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchQuote({ id: quoteId });

      expect(result).toHaveProperty('receivableInvoicingUploadId', 'upload-789');
      expect(result).toHaveProperty('termsConditionsUploadId', undefined);
    });

    it('handles API errors with custom error handler', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const onError = vi.fn<(err: unknown) => void>();
      await fetchQuote({ id: quoteId }, { onError });

      expect(onError).toHaveBeenCalledWith(error);
    });

    it('throws error when no error handler is provided', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchQuote({ id: quoteId })).rejects.toThrow('API Error');
    });

    it('throws error when quote not found', async () => {
      const error = new Error('Not Found');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchQuote({ id: 'non-existent-id' })).rejects.toThrow('Not Found');
    });
  });

  describe(useFetchQuote, () => {
    it('handles custom error callback', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const queryClient = new QueryClient();
      const wrapper = ({ children }: { children: React.ReactNode }) => {
        return createElement(QueryClientProvider, { client: queryClient }, children);
      };

      const onError = vi.fn();
      const { result } = renderHook(() => useFetchQuote({ id: quoteId }, { onError }), { wrapper });

      // Wait for the query to execute and fail
      await waitFor(() => {
        expect(result.current.isError).toBe(true);
      });

      expect(onError).toHaveBeenCalledWith(error);
    });
  });
});
